import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import supportService from "../../api/services/supportService";
import SupportTeamsTab from "../Support/SupportTeamsTab";
import {
  storeSupportAccessMeta,
  storeSupportTabPermissions,
} from "../../constants/supportTabPermissions";

const ALLOWED_PANEL_ROLES = ["Admin", "Developer", "CSR Team Lead"];

export default function SupportTeamsPage() {
  const userRole = localStorage.getItem("adminUserRole") || "Admin";
  const isAdmin = userRole === "Admin" || userRole === "Developer";
  const isDeveloper = userRole === "Developer";
  const isCsrTeamLead = userRole === "CSR Team Lead";
  const canViewCsrStaffList = isAdmin || isCsrTeamLead;
  const canCreateCsrUser = isAdmin || isCsrTeamLead;

  const currentAdminId = (() => {
    try {
      const raw = localStorage.getItem("adminUserDetails");
      if (!raw) return null;
      return JSON.parse(raw)?.id ?? null;
    } catch {
      return null;
    }
  })();

  const [teams, setTeams] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabPermissions, setTabPermissions] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [accessRes, teamsRes, staffRes] = await Promise.all([
        supportService.getAccess(),
        supportService.getTeams(),
        canViewCsrStaffList || isCsrTeamLead
          ? supportService.getStaff()
          : Promise.resolve({ staff: [] }),
      ]);
      const perms = accessRes.access?.tabPermissions || null;
      setTabPermissions(perms);
      if (accessRes.access) {
        storeSupportAccessMeta(accessRes.access);
      } else if (perms) {
        storeSupportTabPermissions(perms);
      }
      setTeams(teamsRes.teams || []);
      const list = staffRes.staff || [];
      setStaff([...new Map(list.map((u) => [u.id, u])).values()]);
    } catch {
      toast.error("Failed to load support teams");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (!ALLOWED_PANEL_ROLES.includes(userRole)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!loading && tabPermissions && !isAdmin && !tabPermissions.teams) {
    return (
      <div className="p-6">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <h1 className="text-lg font-semibold mb-2">Support Teams</h1>
          <p className="text-sm">
            You do not have permission to view the teams module. Contact your team lead or admin.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 w-full">
      <div className="bg-white rounded-xl shadow-xl p-4 md:p-6 min-h-[calc(100vh-120px)]">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Teams</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage teams for CSR and Chat Support — assign roles, choose which tabs
            each role can access, and review performance for the whole team. One CSR Team Lead can
            lead multiple teams (e.g. CSR team + Chat Support team).
          </p>
        </div>
        <SupportTeamsTab
          staff={staff}
          teams={teams}
          loading={loading}
          isAdmin={isAdmin}
          isDeveloper={isDeveloper}
          isCsrTeamLead={isCsrTeamLead}
          canViewCsrStaffList={canViewCsrStaffList}
          canCreateCsrUser={canCreateCsrUser}
          currentAdminId={currentAdminId}
          onRefresh={refresh}
        />
      </div>
    </div>
  );
}
